import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PackageType } from '../../types/partner';
import {
  Box,
  Typography,
  Radio,
  FormControlLabel,
  Button,
  Stack,
  Divider,
  Paper,
  RadioGroup,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Card,
  CardMedia,
  CardContent,
  alpha
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';

interface PackageFormProps {
  onSubmit: (values: { selectedPackage: PackageType }) => void;
  onBack: () => void;
  initialValues?: {
    selectedPackage?: PackageType;
  };
}

const packageSchema = Yup.object().shape({
  selectedPackage: Yup.string().required('Vui lòng chọn một gói dịch vụ')
});

const PackageForm: React.FC<PackageFormProps> = ({ onSubmit, onBack, initialValues = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const formik = useFormik({
    initialValues: {
      selectedPackage: initialValues?.selectedPackage || ('free' as PackageType)
    },
    validationSchema: packageSchema,
    onSubmit: (values) => {
      onSubmit(values);
    }
  });

  const packages = [
    {
      id: 'free',
      name: 'Gói Miễn Phí',
      price: '0đ',
      description: 'Dành cho cửa hàng hiện thị trên Ứng dụng Bookdi',
      image: 'https://images.unsplash.com/photo-1556155092-490a1ba16284?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      color: '#4285f4',
      features: [
        'Đăng ký và tạo hồ sơ doanh nghiệp',
        'Hiển thị trên nền tảng tìm kiếm Bookdi',
        'Quản lý lịch hẹn cơ bản (khách hàng đặt lịch trực tuyến)'
      ],
      limitations: [
        'Chỉ đăng được tối đa 1 dịch vụ',
        'Không có công cụ marketing & khuyến mãi',
        'Không thêm sản phẩm bán hàng',
        'Không có báo cáo chi tiết',
        'Chỉ có 1 tài khoản nhân viên'
      ]
    },
    {
      id: 'premium',
      name: 'Gói Dịch Vụ',
      price: '5.000đ',
      description: 'Dành cho doanh nghiệp muốn quản lý lịch hẹn & khách hàng chuyên nghiệp',
      image: 'https://images.unsplash.com/photo-1565017739313-8b9a40dc4627?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      color: '#34a853',
      features: [
        'Tất cả tính năng của GÓI CƠ BẢN',
        'Đăng 10 dịch vụ trên tìm kiếm Bookdi',
        'Quản lý lịch hẹn nâng cao (đóng bổ lịch, tự động nhắc hẹn)',
        'Thêm nhân viên vào hệ thống để quản lý lịch hẹn',
        'Báo cáo doanh thu, hiệu suất nhân viên',
        'Thêm nhiều Nhân viên cho dịch vụ đang hoạt động kinh doanh',
        'Ứng dụng iOS/Android cho cửa hàng và nhân viên quản lý',
        'Quảng Cáo KHÔNG GIỚI HẠN GROUP REVIEW PHAN RANG NINH THUẬN (Comment)'
      ],
      limitations: []
    }
  ];

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        {!isMobile && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Chọn gói đăng ký
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lựa chọn gói phù hợp với nhu cầu của bạn
            </Typography>
          </Box>
        )}

        <RadioGroup
          name="selectedPackage"
          value={formik.values.selectedPackage}
          onChange={formik.handleChange}
        >
          <Stack spacing={3}>
            {packages.map((pkg) => (
              isMobile ? (
                <Card
                  key={pkg.id}
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: formik.values.selectedPackage === pkg.id 
                      ? pkg.color 
                      : 'transparent',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                  }}
                  onClick={() => formik.setFieldValue('selectedPackage', pkg.id)}
                >
                  <CardMedia
                    component="img"
                    height="120"
                    image={pkg.image}
                    alt={pkg.name}
                    sx={{ 
                      objectFit: 'cover',
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      padding: 1.5,
                    }}
                  >
                    <Radio
                      checked={formik.values.selectedPackage === pkg.id}
                      value={pkg.id}
                      name="selectedPackage"
                      sx={{ 
                        color: '#ffffff', 
                        '&.Mui-checked': { 
                          color: '#ffffff'
                        },
                        backgroundColor: alpha('#000000', 0.3),
                        borderRadius: '50%',
                        padding: 0.75
                      }}
                    />
                  </Box>
                  
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      right: 0, 
                      background: `linear-gradient(to top, ${pkg.color}, transparent)`,
                      height: '60%', 
                      opacity: 0.2
                    }} 
                  />
                  
                  <CardContent sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {pkg.name}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color={pkg.color}>
                        {pkg.price}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {pkg.description}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Tính năng:
                    </Typography>
                    
                    {pkg.features.slice(0, 3).map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                        <CheckIcon sx={{ color: pkg.color, fontSize: 18, mr: 1 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                    
                    {pkg.features.length > 3 && (
                      <Typography variant="body2" color={pkg.color} sx={{ mt: 1, fontWeight: 500 }}>
                        +{pkg.features.length - 3} tính năng khác
                      </Typography>
                    )}
                    
                    {pkg.limitations.length > 0 && (
                      <>
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <Typography variant="body2" color="error" fontWeight={500}>
                            Hạn chế:
                          </Typography>
                        </Box>
                        
                        {pkg.limitations.slice(0, 2).map((limitation, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                            <CancelIcon color="error" fontSize="small" sx={{ mr: 1, fontSize: 18 }} />
                            <Typography variant="body2" color="text.secondary">
                              {limitation}
                            </Typography>
                          </Box>
                        ))}
                        
                        {pkg.limitations.length > 2 && (
                          <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 500 }}>
                            +{pkg.limitations.length - 2} hạn chế khác
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Paper
                  key={pkg.id}
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '2px solid',
                    borderColor: formik.values.selectedPackage === pkg.id ? pkg.color : 'transparent',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      background: formik.values.selectedPackage === pkg.id ? pkg.color : alpha(pkg.color, 0.1),
                      color: '#fff',
                      padding: '5px 15px',
                      borderBottomLeftRadius: 12,
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {pkg.id === 'premium' && <StarIcon fontSize="small" sx={{ mr: 0.5 }} />}
                    {pkg.price}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <FormControlLabel
                      value={pkg.id}
                      control={<Radio sx={{ 
                        color: pkg.color,
                        '&.Mui-checked': {
                          color: pkg.color,
                        }
                      }} />}
                      label=""
                      sx={{ m: 0, mr: 1 }}
                    />
                    
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {pkg.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {pkg.description}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box>
                        {pkg.features.map((feature, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckIcon sx={{ color: pkg.color, mr: 1 }} />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      {pkg.limitations.length > 0 && (
                        <>
                          <Box sx={{ mt: 2, mb: 1 }}>
                            <Typography variant="body2" color="error" fontWeight={500}>
                              Hạn chế:
                            </Typography>
                          </Box>
                          
                          {pkg.limitations.map((limitation, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                              <Typography variant="body2" color="text.secondary">
                                {limitation}
                              </Typography>
                            </Box>
                          ))}
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>
              )
            ))}
          </Stack>
        </RadioGroup>

        {formik.touched.selectedPackage && formik.errors.selectedPackage && (
          <FormHelperText error>{formik.errors.selectedPackage}</FormHelperText>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            fullWidth
            size="large"
            sx={{ 
              py: 1.5, 
              borderRadius: 3,
              textTransform: 'none'
            }}
          >
            Quay lại
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ 
              py: 1.5, 
              borderRadius: 3,
              textTransform: 'none'
            }}
          >
            Tiếp tục
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default PackageForm; 